import { useState } from 'react';

import Stepper from '@axa-japan/design-system-react/Stepper';
import CloseButton from '../components/common/CloseButton';
import useGetForm from '../hooks/useGetForm';
import { useUserContext } from '../providers/UserProvider';
import { AUTH_TYPE } from '../types/auth';
import { FormDataType } from '../utils/formUtils';
import AgreementPage from './AgreementPage';
import DocumentTableSelection, { UPLOAD_TYPE } from './DocumentTableSelection';
import ExternalDeviceUploadForm from './ExternalDeviceUploadForm';
import DocumentFormSelection from './FormSelection';
import UploadFilePage from './UploadFilePage';
import UploadValidationPage from './UploadValidationPage';

const defaultValues: FormDataType = {
  sessionId: undefined,
  handlerConfirmation: false,
  handlerPledge: false,
  receptionNo: '',
  formType: '',
  representativeDocs: [],
  documentsSelected: [],
  uploadType: '',
  pendingUploadDocuments: [],
  failedUploadDocuments: [],
  uploadConfirmation: false,
};

export const ACTIVE_STEP = {
  agreementPage: 'AGREEMENT_PAGE',
  documentFormSelection: 'DOCUMENT_FORM_SELECTION',
  documentTableSelection: 'DOCUMENT_TABLE_SELECTION',
  uploadFilePage: 'UPLOAD_FILE_PAGE',
  uploadValidationPage: 'UPLOAD_VALIDATION_PAGE',
} as const;

const Steps = [
  {
    title: '注意・誓約確認',
  },
  {
    title: '書類選択',
  },
  {
    title: '撮影・送信',
  },
  {
    title: '完了',
  },
];

const HomePage = () => {
  const { token, connectionType } = useUserContext();
  const [visualStep, setVisualStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>(ACTIVE_STEP.agreementPage);
  const [formData, setFormData] = useState<FormDataType>(defaultValues);

  const handleGoto = (page: string) => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    switch (page) {
      case ACTIVE_STEP.agreementPage:
        setVisualStep(0);
        break;
      case ACTIVE_STEP.documentFormSelection:
      case ACTIVE_STEP.documentTableSelection:
        setVisualStep(1);
        break;
      case ACTIVE_STEP.uploadFilePage:
      case ACTIVE_STEP.uploadValidationPage:
        setVisualStep(2);
        break;
    }

    setCurrentStep(page);
  };

  const handleRegisterData = (newData: Partial<FormDataType>, nextPage: string) => {
    const updatedFormData = { ...formData, ...newData };
    if (newData.representativeDocs) {
      updatedFormData.representativeDocs = [...newData.representativeDocs];
    }

    setFormData(updatedFormData);
    handleGoto(nextPage);
  };

  const { data: forms } = useGetForm(!!token);

  return (
    <>
      <div className="md:px-40">
        <Stepper variant="horizontal" steps={Steps} currentStep={visualStep} />
      </div>
      <div className="mt-4 md:px-40 lg:px-20">
        <div className="flex flex-col gap-10 mt-20">
          {currentStep === ACTIVE_STEP.agreementPage && (
            <AgreementPage initialData={formData} onNext={(formData) => handleRegisterData(formData, ACTIVE_STEP.documentFormSelection)} />
          )}
          {currentStep === ACTIVE_STEP.documentFormSelection && (
            <DocumentFormSelection
              initialData={formData}
              onNext={(formData) => {
                if (connectionType === AUTH_TYPE.Internal) {
                  handleRegisterData(formData, ACTIVE_STEP.documentTableSelection);
                } else {
                  const newFormData = { ...formData, uploadType: UPLOAD_TYPE.personalDevice, documentsSelected: formData.representativeDocs };
                  handleRegisterData(newFormData, ACTIVE_STEP.uploadFilePage);
                }
              }}
              onBack={() => handleGoto(ACTIVE_STEP.agreementPage)}
              setFormData={setFormData}
              forms={forms || undefined}
              connectionType={connectionType || AUTH_TYPE.Internal}
            />
          )}
          {currentStep === ACTIVE_STEP.documentTableSelection && (
            <DocumentTableSelection
              initialData={formData}
              onNext={(formData) => handleRegisterData(formData, ACTIVE_STEP.uploadFilePage)}
              onBack={() => handleGoto(ACTIVE_STEP.documentFormSelection)}
              setFormData={setFormData}
              setStepper={setVisualStep}
            />
          )}
          {currentStep === ACTIVE_STEP.uploadFilePage &&
            (formData.uploadType === UPLOAD_TYPE.personalDevice ? (
              <ExternalDeviceUploadForm
                initialData={formData}
                setStepper={setVisualStep}
                onBack={() => {
                  if (connectionType === AUTH_TYPE.Internal) {
                    handleGoto(ACTIVE_STEP.documentTableSelection);
                  } else {
                    handleGoto(ACTIVE_STEP.documentFormSelection);
                  }
                }}
              />
            ) : (
              <UploadFilePage
                initialData={formData}
                onNext={(formData) => handleRegisterData(formData, ACTIVE_STEP.uploadValidationPage)}
                onBack={() => handleGoto(ACTIVE_STEP.documentTableSelection)}
                setFormData={setFormData}
              />
            ))}
          {currentStep === ACTIVE_STEP.uploadValidationPage && (
            <UploadValidationPage
              initialData={formData}
              onNext={(formData) => handleRegisterData(formData, ACTIVE_STEP.documentTableSelection)}
              onBack={() => handleGoto(ACTIVE_STEP.uploadFilePage)}
            />
          )}
        </div>

        <CloseButton currentStep={currentStep} />
      </div>
    </>
  );
};

export default HomePage;
